import React, { useState, useEffect } from 'react';
import TopNav from './TopNav';
import api from '../services/api';

interface EarningsStock {
  symbol: string;
  companyName: string;
  sector: string;
  industry: string;
  lastPrice: number;
  marketCap: number;
  earningsDate: string;
  earningsTime: 'BMO' | 'AMC';
  estimatedEps: number;
  actualEps?: number;
  surprise?: number;
  volume: number;
  avgVolume: number;
  volumeRatio: number;
  impliedVolatility: number;
  optionsVolume: number;
  newsSentiment: number;
  analystRating: string;
  priceTarget: number;
  upside: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  watchlistReason: string;
}

interface EarningsCalendar {
  date: string;
  stocks: EarningsStock[];
}

interface EarningsData {
  watchlist: EarningsStock[];
  calendar: EarningsCalendar[];
  metadata: {
    totalStocks: number;
    lowRisk: number;
    mediumRisk: number;
    highRisk: number;
    lastUpdated: string;
  };
}

const EarningsWatchlist: React.FC = () => {
  const [data, setData] = useState<EarningsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Persist active tab to localStorage
  const getInitialTab = (): 'watchlist' | 'calendar' => {
    const savedTab = localStorage.getItem('earningsWatchlistActiveTab') as 'watchlist' | 'calendar' | null;
    if (savedTab && ['watchlist', 'calendar'].includes(savedTab)) {
      return savedTab;
    }
    return 'watchlist';
  };
  
  const [activeTab, setActiveTab] = useState<'watchlist' | 'calendar'>(getInitialTab());
  
  const handleTabChange = (tab: 'watchlist' | 'calendar') => {
    setActiveTab(tab);
    localStorage.setItem('earningsWatchlistActiveTab', tab);
  };
  const [sortBy, setSortBy] = useState<'earningsDate' | 'riskLevel' | 'upside'>('earningsDate');
  const [filterBy, setFilterBy] = useState<'all' | 'LOW' | 'MEDIUM' | 'HIGH'>('all');

  const fetchData = async (options: { silent?: boolean } = {}) => {
    try {
      if (!options.silent) {
        setLoading(true);
      }
      setError(null);
      
      const response = await api.get('/selectors/earnings-watchlist');
      setData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch earnings watchlist data:', err);
      setError('Failed to load earnings watchlist data');
    } finally {
      if (!options.silent) {
        setLoading(false);
      }
    }
  };

  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchData({ silent: true });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'LOW': return 'text-green-400';
      case 'MEDIUM': return 'text-yellow-400';
      case 'HIGH': return 'text-red-400';
      default: return 'text-neutral-400';
    }
  };

  const getRiskBgColor = (risk: string) => {
    switch (risk) {
      case 'LOW': return 'bg-green-900/30';
      case 'MEDIUM': return 'bg-yellow-900/30';
      case 'HIGH': return 'bg-red-900/30';
      default: return 'bg-neutral-900/30';
    }
  };

  const getEarningsTimeColor = (time: string) => {
    return time === 'BMO' ? 'text-blue-400' : 'text-purple-400';
  };

  const getEarningsTimeIcon = (time: string) => {
    return time === 'BMO' ? 'AM' : 'PM';
  };

  const getDaysUntilEarnings = (earningsDate: string) => {
    const today = new Date();
    const earnings = new Date(earningsDate);
    const diffTime = earnings.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredStocks = data?.watchlist.filter(stock => {
    if (filterBy === 'all') return true;
    return stock.riskLevel === filterBy;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'earningsDate':
        return new Date(a.earningsDate).getTime() - new Date(b.earningsDate).getTime();
      case 'riskLevel':
        const riskOrder = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3 };
        return riskOrder[a.riskLevel] - riskOrder[b.riskLevel];
      case 'upside':
        return b.upside - a.upside;
      default:
        return 0;
    }
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading Earnings Watchlist...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <div className="mb-4">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          </div>

          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Earnings Watchlist</h1>
              <p className="text-neutral-400">
                Top earnings-day stocks for monitoring • Last updated {data?.metadata.lastUpdated ? new Date(data.metadata.lastUpdated).toLocaleTimeString() : 'Unknown'}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`inline-flex items-center px-4 py-2 rounded-lg border border-cyan-500 text-cyan-400 hover:bg-cyan-600/10 transition-colors ${refreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {refreshing ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-400 mr-2"></span>
                  Refreshing…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M5 13a7 7 0 0112-5M19 11a7 7 0 01-12 5" />
                  </svg>
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Summary Cards */}
          {data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-cyan-500">
                <p className="text-xs text-neutral-400 uppercase tracking-wide">Total Tracked</p>
                <p className="text-2xl font-bold text-white mt-1">{data.metadata.totalStocks}</p>
                <p className="text-xs text-neutral-500 mt-1">earnings plays</p>
              </div>
              
              <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-green-500">
                <p className="text-xs text-neutral-400 uppercase tracking-wide">Low Risk</p>
                <p className="text-2xl font-bold text-green-400 mt-1">{data.metadata.lowRisk}</p>
                <p className="text-xs text-green-400/60 mt-1">safe plays</p>
              </div>
              
              <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-yellow-500">
                <p className="text-xs text-neutral-400 uppercase tracking-wide">Medium Risk</p>
                <p className="text-2xl font-bold text-yellow-400 mt-1">{data.metadata.mediumRisk}</p>
                <p className="text-xs text-yellow-400/60 mt-1">moderate plays</p>
              </div>
              
              <div className="bg-neutral-800 rounded-lg p-4 border-l-4 border-red-500">
                <p className="text-xs text-neutral-400 uppercase tracking-wide">High Risk</p>
                <p className="text-2xl font-bold text-red-400 mt-1">{data.metadata.highRisk}</p>
                <p className="text-xs text-red-400/60 mt-1">volatile plays</p>
              </div>
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <div>
                <p className="text-purple-300 font-medium text-sm">Earnings Zone Strategy Active</p>
                <p className="text-neutral-400 text-xs mt-1">
                  Stocks in BLACKOUT or IMMINENT zones are excluded from the trading universe. APPROACH zone stocks have reduced position sizes.
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="flex space-x-4">
              <button
                onClick={() => handleTabChange('watchlist')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'watchlist'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Watchlist
              </button>
              <button
                onClick={() => handleTabChange('calendar')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'calendar'
                    ? 'bg-cyan-600 text-white'
                    : 'bg-neutral-800 text-neutral-400 hover:text-white'
                }`}
              >
                Calendar View
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'watchlist' && (
            <div className="bg-neutral-800 rounded-lg p-6">
              {/* Controls */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Sort by</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600"
                  >
                    <option value="earningsDate">Earnings Date</option>
                    <option value="riskLevel">Risk Level</option>
                    <option value="upside">Upside Potential</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Filter by Risk</label>
                  <select
                    value={filterBy}
                    onChange={(e) => setFilterBy(e.target.value as any)}
                    className="bg-neutral-700 text-white px-3 py-2 rounded-lg border border-neutral-600"
                  >
                    <option value="all">All Risk Levels</option>
                    <option value="LOW">Low Risk</option>
                    <option value="MEDIUM">Medium Risk</option>
                    <option value="HIGH">High Risk</option>
                  </select>
                </div>
              </div>

              {/* Stock Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-300 border-b border-neutral-700 bg-neutral-800/50">
                      <th className="py-3 px-3 sticky left-0 bg-neutral-800">Symbol</th>
                      <th className="py-3 px-2">Countdown</th>
                      <th className="py-3 px-2">Time</th>
                      <th className="py-3 px-2">Risk</th>
                      <th className="py-3 px-2">Price</th>
                      <th className="py-3 px-2">Upside</th>
                      <th className="py-3 px-2">Est EPS</th>
                      <th className="py-3 px-2">Volume</th>
                      <th className="py-3 px-2">Sector</th>
                      <th className="py-3 px-2">Company</th>
                      <th className="py-3 px-2">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-700/50">
                    {filteredStocks.map((stock) => {
                      const daysUntil = getDaysUntilEarnings(stock.earningsDate);
                      return (
                        <tr key={stock.symbol} className={`hover:bg-neutral-700/30 transition-colors ${
                          daysUntil <= 1 ? 'bg-red-900/10' : ''
                        }`}>
                          <td className="py-3 px-3 sticky left-0 bg-neutral-800">
                            <div className="font-semibold text-white">{stock.symbol}</div>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              daysUntil <= 0 ? 'bg-red-600 text-white animate-pulse' :
                              daysUntil === 1 ? 'bg-orange-600 text-white' :
                              daysUntil <= 3 ? 'bg-yellow-600 text-black' :
                              daysUntil <= 7 ? 'bg-blue-900/50 text-blue-300' :
                              'bg-neutral-700 text-neutral-300'
                            }`}>
                              {daysUntil <= 0 ? 'TODAY' : daysUntil === 1 ? 'TOMORROW' : `${daysUntil}d`}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              stock.earningsTime === 'BMO' ? 'bg-blue-900/30 text-blue-400' : 'bg-purple-900/30 text-purple-400'
                            }`}>
                              {stock.earningsTime === 'BMO' ? 'Pre-Market' : 'After-Hours'}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              stock.riskLevel === 'LOW' ? 'bg-green-900/50 text-green-300 border border-green-500/30' :
                              stock.riskLevel === 'MEDIUM' ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-500/30' :
                              'bg-red-900/50 text-red-300 border border-red-500/30'
                            }`}>
                              {stock.riskLevel}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-white font-mono">${stock.lastPrice.toFixed(2)}</td>
                          <td className="py-3 px-2">
                            <span className={`font-mono text-sm font-bold ${
                              stock.upside > 0.1 ? 'text-green-400' : 
                              stock.upside > 0 ? 'text-green-300' : 
                              stock.upside < -0.1 ? 'text-red-400' :
                              'text-red-300'
                            }`}>
                              {stock.upside > 0 ? '+' : ''}{(stock.upside * 100).toFixed(1)}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-neutral-300 font-mono">
                            ${stock.estimatedEps.toFixed(2)}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center space-x-1">
                              <div className="w-10 bg-neutral-600 rounded-full h-1.5">
                                <div 
                                  className={`h-1.5 rounded-full ${
                                    stock.volumeRatio > 2 ? 'bg-red-400' : 
                                    stock.volumeRatio > 1.5 ? 'bg-yellow-400' : 
                                    'bg-green-400'
                                  }`}
                                  style={{ width: `${Math.min(stock.volumeRatio * 40, 100)}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-neutral-400">{stock.volumeRatio.toFixed(1)}x</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-neutral-400 text-xs">{stock.sector}</td>
                          <td className="py-3 px-2 text-neutral-300 text-xs max-w-24 truncate" title={stock.companyName}>
                            {stock.companyName}
                          </td>
                          <td className="py-3 px-2">
                            <div className="text-xs text-neutral-400 max-w-24 truncate" title={stock.watchlistReason}>
                              {stock.watchlistReason}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredStocks.length === 0 && (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-neutral-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-neutral-400 text-lg">No earnings scheduled</p>
                  <p className="text-neutral-500 text-sm mt-2">Check back during market hours for upcoming earnings</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'calendar' && data && (
            <div className="bg-neutral-800 rounded-lg p-6">
              <h3 className="text-xl font-bold text-white mb-4">Earnings Calendar</h3>
              
              <div className="space-y-6">
                {data.calendar.map((day) => (
                  <div key={day.date} className="bg-neutral-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-white mb-3">
                      {new Date(day.date).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {day.stocks.map((stock) => (
                        <div key={stock.symbol} className="bg-neutral-600 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-semibold text-white">{stock.symbol}</div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskBgColor(stock.riskLevel)} ${getRiskColor(stock.riskLevel)}`}>
                              {stock.riskLevel}
                            </span>
                          </div>
                          
                          <div className="text-sm text-neutral-300 space-y-1">
                            <div className="flex justify-between">
                              <span>Price:</span>
                              <span className="text-white">${stock.lastPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Time:</span>
                              <span className={getEarningsTimeColor(stock.earningsTime)}>
                                {getEarningsTimeIcon(stock.earningsTime)} {stock.earningsTime}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Upside:</span>
                              <span className={stock.upside > 0 ? 'text-green-400' : 'text-red-400'}>
                                {stock.upside > 0 ? '+' : ''}{(stock.upside * 100).toFixed(1)}%
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Volume:</span>
                              <span className="text-white">{stock.volumeRatio.toFixed(1)}x avg</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EarningsWatchlist;
